import React, { useState } from 'react';
import {
    Modal, Box, Typography, Button, TextField,
    CircularProgress, Alert, IconButton
} from '@mui/material';
import { FaShield } from 'react-icons/fa6';
import { FiUploadCloud, FiX, FiCheckCircle } from 'react-icons/fi';
import { IoCloseCircleOutline } from 'react-icons/io5';
import axios from 'axios';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 480,
    maxWidth: '92vw',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'rgba(15, 18, 28, 0.96)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '24px',
    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.7), 0 0 30px rgba(239, 68, 68, 0.15)',
    p: { xs: 3, sm: 4 },
    outline: 'none',
};

export default function ApelarBlacklistModal({ open, onClose, isMobile, onSuccess }) {
    const [motivo, setMotivo] = useState('');
    const [archivo, setArchivo] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exito, setExito] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Por favor selecciona un archivo de imagen válido (PNG, JPG, WEBP).');
                return;
            }
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError('La imagen es demasiado grande. El tamaño máximo es 10MB.');
                return;
            }
            setError('');
            setArchivo(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleRemoveFile = () => {
        setArchivo(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!motivo.trim()) {
            setError('Por favor describe detalladamente el motivo de tu apelación.');
            return;
        }
        if (!archivo) {
            setError('Es obligatorio adjuntar una imagen o captura de prueba.');
            return;
        }

        setLoading(true);
        setError('');

        const nombre = localStorage.getItem('kick_user') || 'Usuario';
        const email = localStorage.getItem('kick_mail') || '';

        try {
            // Helper to fetch IP and Fingerprint
            let ip = '';
            try {
                const ipRes = await fetch('https://api.ipify.org?format=json');
                const ipData = await ipRes.json();
                ip = ipData.ip || '';
            } catch {
                ip = '0.0.0.0';
            }

            let fingerprint = '';
            try {
                const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
                const fp = await FingerprintJS.load();
                const result = await fp.get();
                fingerprint = result.visitorId || '';
            } catch (errFp) {
                console.warn('Fingerprint error:', errFp);
            }

            // 1. Upload image to server
            let imageUrl = '';
            try {
                const formData = new FormData();
                formData.append('imagen', archivo);
                const uploadRes = await fetch('https://backend.eldenguee.com/solicitudes/upload', {
                    method: 'POST',
                    body: formData,
                });
                if (uploadRes.ok) {
                    const data = await uploadRes.json();
                    imageUrl = data.url || data.fileUrl || data.secure_url || data.filename || '';
                }
            } catch (err) {
                console.warn('Fallback al subir imagen:', err);
            }

            // Fallback preview URL if upload didn't return URL
            if (!imageUrl && previewUrl) {
                imageUrl = previewUrl;
            }

            // 2. Submit appeal request (tries /submit-appeal for dedicated table, falls back to /submit-request)
            const payload = {
                nombre,
                email,
                motivo: motivo.trim(),
                imagen: imageUrl,
                offerScreenshot: imageUrl,
                bnbScreenshot: imageUrl,
                bnbAddress: motivo.trim(),
                ip,
                fingerprint,
                casino: 'blacklist',
                tipo: 'apelacion',
                status: 'pending',
                createdAt: new Date().toISOString()
            };

            let response;
            try {
                response = await axios.post('https://backend.eldenguee.com/solicitudes/submit-appeal', payload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            } catch (appealErr) {
                // Fallback to /submit-request if dedicated endpoint doesn't exist yet
                response = await axios.post('https://backend.eldenguee.com/solicitudes/submit-request', payload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            if (response.data && response.data.error) {
                throw new Error(response.data.error);
            }

            setExito(true);
            if (onSuccess) onSuccess();

            setTimeout(() => {
                setExito(false);
                setMotivo('');
                handleRemoveFile();
                onClose();
            }, 2500);

        } catch (err) {
            console.error('Error al enviar la apelación:', err);
            const serverMessage = err.response?.data?.error || err.message;
            setError(serverMessage || 'Ocurrió un error al enviar la apelación. Por favor intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={loading ? undefined : onClose}>
            <Box sx={modalStyle}>
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                >
                    <IoCloseCircleOutline size={26} />
                </IconButton>

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(185, 28, 28, 0.3) 100%)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 14px',
                        boxShadow: '0 0 20px rgba(239, 68, 68, 0.3)'
                    }}>
                        <FaShield size={28} color="#ef4444" />
                    </Box>
                    <Typography sx={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 800,
                        fontSize: { xs: '1.4rem', sm: '1.75rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em'
                    }}>
                        Apelar a la Blacklist
                    </Typography>
                    <Typography sx={{
                        fontFamily: 'Outfit, sans-serif',
                        fontSize: '0.88rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        mt: 0.5,
                        maxWidth: '380px',
                        mx: 'auto',
                        lineHeight: 1.4
                    }}>
                        Tu cuenta está sancionada. Completa el formulario con tu justificación y un comprobante para solicitar la revisión.
                    </Typography>
                </Box>

                {exito ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <FiCheckCircle size={54} color="#00ff73" style={{ marginBottom: '16px' }} />
                        <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.3rem', fontWeight: 700, color: '#ffffff' }}>
                            ¡Solicitud Enviada con Éxito!
                        </Typography>
                        <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                            Tu apelación ha sido registrada. Un administrador la revisará a la brevedad.
                        </Typography>
                    </Box>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px', fontFamily: 'Outfit, sans-serif' }}>
                                {error}
                            </Alert>
                        )}

                        {/* Motivo */}
                        <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{
                                fontFamily: 'Outfit, sans-serif',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: 'rgba(255, 255, 255, 0.85)',
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Motivo de la Apelación <span style={{ color: '#ef4444' }}>*</span>
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                placeholder="Explica detalladamente las razones de tu apelación (por qué tu inclusión en la blacklist fue un error o por qué deberías ser removido)..."
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                disabled={loading}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        color: '#ffffff',
                                        fontFamily: 'Outfit, sans-serif',
                                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                        borderRadius: '14px',
                                        border: '1px solid rgba(255, 255, 255, 0.12)',
                                        transition: 'border-color 0.2s',
                                        '&:hover': {
                                            borderColor: 'rgba(239, 68, 68, 0.4)'
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#ef4444',
                                            boxShadow: '0 0 12px rgba(239, 68, 68, 0.25)'
                                        }
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                                }}
                            />
                        </Box>

                        {/* Archivo Imagen */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{
                                fontFamily: 'Outfit, sans-serif',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: 'rgba(255, 255, 255, 0.85)',
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Imagen / Captura de Prueba <span style={{ color: '#ef4444' }}>*</span>
                            </Typography>

                            {previewUrl ? (
                                <Box sx={{
                                    position: 'relative',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    maxHeight: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.4)'
                                }}>
                                    <img
                                        src={previewUrl}
                                        alt="Vista previa"
                                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveFile}
                                        disabled={loading}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                            color: '#ef4444',
                                            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.3)', color: '#ffffff' }
                                        }}
                                    >
                                        <FiX size={18} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box
                                    component="label"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 3,
                                        borderRadius: '16px',
                                        border: '2px dashed rgba(239, 68, 68, 0.4)',
                                        backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.25s ease',
                                        '&:hover': {
                                            borderColor: '#ef4444',
                                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                        disabled={loading}
                                    />
                                    <FiUploadCloud size={32} color="#ef4444" style={{ marginBottom: '8px' }} />
                                    <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '0.92rem', color: '#ffffff' }}>
                                        Haz clic para seleccionar una imagen
                                    </Typography>
                                    <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.5)', mt: 0.5 }}>
                                        Formatos soportados: PNG, JPG, WEBP (Máx 10MB)
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading || !motivo.trim() || !archivo}
                            sx={{
                                py: 1.6,
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: '#ffffff',
                                fontFamily: 'Outfit, sans-serif',
                                fontWeight: 800,
                                fontSize: '1rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                    boxShadow: '0 6px 25px rgba(239, 68, 68, 0.6)',
                                    transform: 'scale(1.01)'
                                },
                                '&.Mui-disabled': {
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    color: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Enviar Solicitud de Apelación'
                            )}
                        </Button>
                    </form>
                )}
            </Box>
        </Modal>
    );
}
